import { useState } from "react";

import { axiosInstance } from "@refinedev/simple-rest";

import type { SandboxEntityType } from "../types/entities";
import type { InteractionType } from "../types/interactions";

const SANDBOX_ENTITY_TYPES = new Set<string>([
  "artist",
  "genre",
  "venue",
  "event",
]);

const INTERACTION_TYPES = new Set<string>([
  "view",
  "follow",
  "share",
  "screen_captured",
  "ticket_view",
  "ticket_purchase",
  "offer_view",
]);

interface EmbeddingTerm {
  id: string;
  weight: number;
  entityType: string;
  origin?: string;
}

interface LatestEmbeddingDto {
  embedding: number[];
  semanticText?: string;
  createdAt?: string;
  updatedAt?: string;
  terms?: EmbeddingTerm[];
  historicalDocumentId: string;
  latest: boolean;
}

export interface UserStub {
  interactionId: string;
  entityType: SandboxEntityType;
  entityId: string;
  interactionType: InteractionType;
}

export function useUserInteractions(apiUrl: string) {
  const [isLoading, setIsLoading] = useState(false);

  async function fetchUser(
    userId: string,
  ): Promise<{ embedding: number[] | null; stubs: UserStub[] }> {
    setIsLoading(true);
    try {
      const embeddingResp = await axiosInstance.get(
        `${apiUrl}/users/${userId}/embeddings/latest`,
        { params: { "api-version": "1.0" } },
      );

      const dto: LatestEmbeddingDto = embeddingResp.data;
      const embedding = dto.embedding ?? null;

      const stubs: UserStub[] = (dto.terms ?? [])
        .filter((t) => SANDBOX_ENTITY_TYPES.has(t.entityType))
        .map((term) => ({
          interactionId: crypto.randomUUID(),
          entityType: term.entityType as SandboxEntityType,
          entityId: term.id,
          interactionType: INTERACTION_TYPES.has(term.origin ?? "")
            ? (term.origin as InteractionType)
            : "view",
        }));

      return { embedding, stubs };
    } catch (err: any) {
      const message =
        err?.response?.data?.message ?? err?.message ?? "Failed to fetch user";
      throw new Error(message);
    } finally {
      setIsLoading(false);
    }
  }

  return { isLoading, fetchUser };
}
